import { Stack, SxProps, Theme, Typography } from '@mui/material';
import { useMemo } from 'react';

import { FileCheck02Icon } from '@/components/icons/FileCheck02Icon';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { DOCUMENT_TYPE_OPTIONS } from '@/modules/documents/documents.constants';
import { COLORS } from '@/styles/tokens/colors';
import { DocumentType } from '@/types/schema';
import {
  formatDateToMonDDYYYY,
  formatDateToMonDDYYYYWithTime,
} from '@/utils/formatting/dates';

export interface FileRepresentationProps {
  sx?: SxProps<Theme>;
  fileName: string;
  fileNameTypographyClampedLines?: number;
  uploadedAt?: Date;
  uploadedAtFormat?: 'date-only' | 'date-time';
  uploadedBy?: string;
  actions?: React.ReactNode;
  documentType?: DocumentType | undefined;
  hideIcon?: boolean;
}

export function FileRepresentation({
  sx,
  fileName,
  fileNameTypographyClampedLines = 1,
  documentType,
  uploadedAt,
  uploadedAtFormat = 'date-time',
  uploadedBy,
  actions,
  hideIcon = false,
}: FileRepresentationProps) {
  const documentTypeDisplay = useMemo(
    () =>
      DOCUMENT_TYPE_OPTIONS.find(
        (documentTypeOption) => documentTypeOption.value === documentType
      )?.display,
    [documentType]
  );

  const subtitleText = useMemo(() => {
    if (!uploadedAt || !uploadedBy) return '';

    const uploadedAtDisplay =
      uploadedAtFormat === 'date-time'
        ? formatDateToMonDDYYYYWithTime(uploadedAt)
        : formatDateToMonDDYYYY(uploadedAt);

    return `Uploaded ${uploadedAtDisplay} by ${uploadedBy}`;
  }, [uploadedAt, uploadedAtFormat, uploadedBy]);

  return (
    <Stack
      direction="row"
      p={2}
      alignItems="center"
      justifyContent="space-between"
      border={`solid ${COLORS.GRAY[200]} 1px`}
      borderRadius={1}
      sx={{
        width: '100%',
        overflow: 'hidden',
        background: 'white',
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        width="100%"
        overflow="hidden"
      >
        {!hideIcon && <FileCheck02Icon color={COLORS.TEXT.SECONDARY} />}
        <Stack spacing={0.5} width="100%" overflow="hidden">
          <TypographyClamped
            variant="title-s"
            lines={fileNameTypographyClampedLines}
            tooltipProps={{
              placement: 'top',
              title: fileName,
            }}
          >
            {fileName}
          </TypographyClamped>
          {documentTypeDisplay && (
            <Typography variant="body-xs" component="div">
              {documentTypeDisplay}
            </Typography>
          )}
          {subtitleText && (
            <Typography variant="body-xs" component="div">
              {subtitleText}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {actions && actions}
      </Stack>
    </Stack>
  );
}
